// Google Analytics 4 for atlas-web (VIT-17882). Loaded client-side from
// app/entry.client.tsx the same way Datadog RUM/Logs are (app/instrumentation.client.ts)
// rather than as a <script> in the document head: one production gate, read from one env
// source, and no build-time env dependency for the prerendered pages (/terms, /privacy, …).
// The cost is that gtag.js starts loading after hydration rather than during the initial
// parse, which loses a little fidelity on very fast bounces.
//
// Deliberately NOT named `analytics.client.ts`, unlike its Datadog neighbours. React Router
// replaces a `*.client.ts` module with an empty one in the server build, and this module is
// reached from the server-rendered root route (app/root.tsx → app/use-page-view.ts). Under
// the suffix, the SSR copy of `usePageView` silently lost its `trackPageView` call — no
// build error, because the import is elided rather than left dangling. Nothing calls into
// here server-side today, so that was harmless, but the guard belongs in the runtime env
// check below, not in a filename.
//
// Two deliberate constraints, both privacy-driven — see the GA property checklist in
// README.md. Note the privacy policy discloses only that GA runs, and deliberately does not
// describe constraint 2 (VIT-17882 decision), so nothing outside this file states the rule:
//
//  1. No cookie banner. vit.al serves US traffic only (app/components/us-only-notice.tsx),
//     so there is no GDPR-style prior-consent duty. Consent Mode v2 is still initialised
//     with every advertising signal DENIED, so nothing measured here is usable for ads,
//     and adding a real opt-in banner later only has to flip `analytics_storage`.
//
//  2. Nothing that names a visitor's health interest reaches Google. The results URL
//     carries the searched specialty (`/us/portland--or/dermatology`) and its <title>
//     names it in full ("Dermatologists in Portland, OR | …", see routes/results.tsx) —
//     under the Washington My Health My Data Act that is consumer health data, since it
//     evidences "seeking health care services". Both are redacted before send, and the
//     query string is dropped outright because the results page carries `?insurance=`.
//     Provider pages are deliberately NOT redacted: the NPI is the page identity and
//     per-provider metrics are the point of the SEO work (VIT-17882 decision).
//
//     The redacted values are also installed as hit-wide defaults, so GA's own events
//     (`session_start`, `user_engagement`, scroll, …) inherit them instead of re-reading the
//     document — see `trackPageView`. What the guarantee cannot survive is the GA admin UI,
//     which this repo cannot reach. In particular Enhanced measurement's "page
//     changes based on browser history events" sends its OWN `page_view` on every
//     `pushState` with the real URL and title; `send_page_view: false` below does not stop
//     it (it only suppresses the config-time hit). Form interactions are the same kind of
//     problem in a different parameter: `form_destination` comes off the form's `action`,
//     which for the search form resolves to the current document URL. Both listeners have to
//     be off on the property — see the checklist in README.md, which is part of shipping
//     this, not an optional extra.
//
// One standing obligation this file cannot enforce on its own: the privacy policy commits
// to running NO analytics on pages where a visitor uploads or views health information.
// Atlas upload does not exist in this app yet — when it lands, those routes have to be
// excluded here (bail out of `trackPageView`, and pass `send_page_view: false` keeps gtag
// from filling the gap by itself).

const { VITE_ENVIRONMENT } = import.meta.env

// GA4 measurement id for the vit.al property. Public by design — it travels in the
// gtag.js request URL of every page view — so it is inlined here exactly like the
// Datadog browser client token and RUM application id in app/instrumentation.client.ts.
const MEASUREMENT_ID = 'G-JJ7PVN2PJ8'

/**
 * Stands in for the searched specialty in `page_location`.
 *
 * Parenthesised to read as a redaction alongside GA's own `(not set)` / `(direct)`
 * placeholders, and because parentheses are sub-delimiters in RFC 3986 paths and so need
 * no percent-encoding — a bracketed `[specialty]` would arrive in reports as `%5B…%5D`.
 */
export const SPECIALTY_PLACEHOLDER = '(specialty)'

/** Replaces the results-page `<title>`, which spells the specialty out in full. */
export const REDACTED_TITLE = 'Provider search results'

/**
 * Redact the specialty segment of a results URL, leaving every other path untouched.
 *
 * Positional, NOT matched against the specialty registry (app/specialties.ts): a typo'd
 * or retired slug still lands on this route shape and still evidences a health interest,
 * so the fail-safe is to redact whatever occupies that slot.
 *
 * `/us/provider/npi-…` has the same segment count and is excluded explicitly — see the
 * URL grammar in docs/url-structure.md.
 */
export const redactPathname = (pathname: string): string => {
  const segments = pathname.split('/').filter((segment) => segment !== '')

  const [country, location, specialty] = segments
  if (segments.length !== 3 || country !== 'us' || location === 'provider' || specialty === undefined) {
    return pathname
  }

  return `/us/${location}/${SPECIALTY_PLACEHOLDER}`
}

type PageViewParams = {
  page_location: string
  page_title: string
  page_referrer?: string
}

/**
 * Build the `page_view` parameters, with every value sanitised.
 *
 * `page_location` is rebuilt from the origin and the redacted path, so the query string
 * and fragment are dropped by construction rather than by a filter someone has to
 * remember to update. `page_title` follows the path: a redacted path means the title
 * named the specialty too.
 *
 * A same-origin referrer goes through the same redaction — walking from a results page to
 * a provider page would otherwise hand Google the specialty as `page_referrer`. A
 * cross-origin referrer is passed through whole: it is acquisition data, it is not ours to
 * rewrite, and it says nothing about health. An unparsable referrer is omitted rather than
 * guessed at.
 */
export const toPageViewParams = ({
  origin,
  pathname,
  referrer,
  title,
}: {
  origin: string
  pathname: string
  referrer: string
  title: string
}): PageViewParams => {
  const redactedPath = redactPathname(pathname)

  const params: PageViewParams = {
    page_location: `${origin}${redactedPath}`,
    page_title: redactedPath === pathname ? title : REDACTED_TITLE,
  }

  const pageReferrer = toReferrer(referrer, origin)
  if (pageReferrer !== undefined) {
    params.page_referrer = pageReferrer
  }

  return params
}

const toReferrer = (referrer: string, origin: string): string | undefined => {
  if (referrer === '') {
    return undefined
  }

  let parsed: URL
  try {
    // Resolved against `origin` so a bare path — which is what the router hands us for a
    // client navigation — parses as same-origin instead of throwing.
    parsed = new URL(referrer, origin)
  } catch {
    return undefined
  }

  return parsed.origin === origin ? `${origin}${redactPathname(parsed.pathname)}` : referrer
}

declare global {
  interface Window {
    dataLayer?: IArguments[]
  }
}

/**
 * gtag.js recognises a queued command only when the `dataLayer` entry is a real
 * `arguments` object; a plain array is pushed without complaint and then ignored. There is
 * no way to construct one without the keyword, hence this shim and its one suppression.
 */
function toArgumentsObject(..._args: readonly unknown[]): IArguments {
  // biome-ignore lint/complexity/noArguments: the `arguments` object IS the payload gtag.js expects — see above.
  return arguments
}

const gtag = (...args: readonly unknown[]): void => {
  window.dataLayer ??= []
  window.dataLayer.push(toArgumentsObject(...args))
}

// GA only runs in production, mirroring RUM in app/instrumentation.client.ts: a PR preview
// stage would otherwise pollute the property with synthetic traffic, and the local dev
// stage has no measurement to add. Everything exported below is a no-op elsewhere.
const isEnabled = (): boolean => VITE_ENVIRONMENT === 'production'

let initialised = false

/**
 * Load gtag.js and record the initial page view. Idempotent, and a no-op outside
 * production.
 */
export const initAnalytics = (): void => {
  if (!isEnabled() || initialised) {
    return
  }
  initialised = true

  // Consent defaults must be queued BEFORE `config`, otherwise the first hit is sent
  // under gtag.js's own defaults (all granted). Queue order is what matters, not script
  // load order — these land in `dataLayer` and are drained once gtag.js arrives.
  //
  // Every advertising signal is denied, and `allow_google_signals` /
  // `allow_ad_personalization_signals` are off on top of that: consent mode governs
  // storage, those two govern whether GA may enrich or share the data for ads at all.
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'granted',
  })

  gtag('js', new Date())

  gtag('config', MEASUREMENT_ID, {
    // Page views are sent by hand, from here and from app/use-page-view.ts, so that every
    // one of them goes through `toPageViewParams`. gtag's automatic page view would send the
    // raw URL and title. This suppresses the config-time hit only: hits GA generates on its
    // own are covered by the `gtag('set', …)` in `trackPageView`, and the enhanced
    // measurement history listener by an admin setting — see the header comment for both.
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  })

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`
  document.head.appendChild(script)

  // The document title is the server-rendered one at this point (this runs before
  // hydration), so it is already correct for the landing URL.
  trackPageView()
}

/**
 * Send one sanitised `page_view`, and make its sanitised values the default for every other
 * hit sent from this page.
 *
 * The `set` is not redundant with the event parameters. gtag derives `page_location`,
 * `page_title` and `page_referrer` from the document for EVERY hit, not just page views, and
 * an event parameter only overrides them on the one event that carries it. So without the
 * `set`, `session_start`, `first_visit`, `user_engagement` and any enhanced measurement event
 * (scroll, outbound click, file download) would each report the real URL — specialty and
 * `?insurance=` included — while the page view next to them reported the redacted one. The
 * first three of those cannot be turned off at all, in code or in the admin UI.
 *
 * `set` values persist until overwritten, which is what makes this safe across a client
 * navigation: the next call replaces all three before the next page view goes out.
 *
 * `referrer` overrides `document.referrer`, which a client-side navigation never updates —
 * it stays whatever external page opened the tab, for the life of the SPA session.
 * app/use-page-view.ts passes the path actually navigated away from.
 */
export const trackPageView = (referrer?: string): void => {
  if (!isEnabled()) {
    return
  }

  const params = toPageViewParams({
    origin: window.location.origin,
    pathname: window.location.pathname,
    referrer: referrer ?? document.referrer,
    title: document.title,
  })

  gtag('set', params)
  gtag('event', 'page_view', params)
}
